import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import bannerImg from '../assets/ChatGPT Image May 5, 2026, 08_34_35 PM.png';

const STORAGE_KEY = 'tabtrips_extranet_promo_banner_dismissed_v1';

function readDismissed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Promo banner. Hidden permanently on this device after dismiss (X).
 * `listPropertyPath`: `/partner-registration` on login (public); default `/add-property` when logged in.
 */
const ExtranetPromoBanner = ({ listPropertyPath = '/add-property' }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => !readDismissed());

  const dismiss = useCallback(() => {
    persistDismissed();
    setVisible(false);
  }, []);

  const goListProperty = useCallback(() => {
    setVisible(false);
    navigate(listPropertyPath);
  }, [navigate, listPropertyPath]);

  const goContact = useCallback(() => {
    setVisible(false);
    navigate('/support');
  }, [navigate]);

  const overlaySx = useMemo(
    () => ({
      position: 'absolute',
      cursor: 'pointer',
      border: 'none',
      padding: 0,
      background: 'transparent',
      WebkitTapHighlightColor: 'transparent',
      '&:focus-visible': {
        outline: '2px solid #FF7700',
        outlineOffset: 2,
      },
    }),
    []
  );

  if (!visible) return null;

  const cardShadow =
    '0 0 0 1px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), 0 12px 32px rgba(0, 0, 0, 0.22), 0 28px 56px rgba(0, 0, 0, 0.35)';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1350,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 0.5, sm: 2, md: 3 },
        py: { xs: 1, sm: 2, md: 2.5 },
        bgcolor: 'rgba(12, 12, 16, 0.58)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      role="dialog"
      aria-label="TabTrips promotion"
      aria-modal="false"
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: 'min(680px, calc(100vw - 32px))',
            md: 'min(840px, calc(100vw - 80px))',
            lg: 'min(920px, calc(100vw - 112px))',
          },
          maxHeight: {
            xs: 'min(94vh, 960px)',
            sm: 'min(88vh, 800px)',
            md: 'min(82vh, 720px)',
            lg: 'min(80vh, 700px)',
          },
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          overflow: 'hidden',
          bgcolor: '#fff',
          border: '1px solid rgba(38, 38, 42, 0.22)',
          boxShadow: cardShadow,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <IconButton
          onClick={dismiss}
          aria-label="Close promotion"
          sx={{
            position: 'absolute',
            top: { xs: 6, sm: 10 },
            right: { xs: 6, sm: 10 },
            zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.94)',
            color: '#1a1a1a',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            '&:hover': { bgcolor: '#fff' },
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
        </IconButton>

        {/* Scroll outer area; inner box height = image height so % overlays match the artwork */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            bgcolor: '#fafafa',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={bannerImg}
              alt="TabTrips Extranet: 30 days 0% commission on new property signup"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: {
                  xs: 'min(92vh, 900px)',
                  sm: 'min(84vh, 760px)',
                  md: 'min(78vh, 680px)',
                  lg: 'min(76vh, 660px)',
                },
                objectFit: 'contain',
                objectPosition: 'center top',
                display: 'block',
                verticalAlign: 'top',
              }}
            />

            {/* List left; Contact right — contact higher z-index so edges never steal clicks */}
            <Box
              component="button"
              type="button"
              onClick={goListProperty}
              aria-label="List your property now"
              sx={{
                ...overlaySx,
                zIndex: 1,
                left: '4%',
                bottom: '7%',
                width: '40%',
                height: '14%',
                borderRadius: '999px',
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={goContact}
              aria-label="Contact us"
              sx={{
                ...overlaySx,
                zIndex: 2,
                left: '46%',
                bottom: '7%',
                width: '28%',
                height: '14%',
                borderRadius: '999px',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExtranetPromoBanner;
